// RouteManager.tsx
import { subscribeWeb, unsubscribeWeb } from "inventory-web";
import { useEffect, useLayoutEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { isLanguageSegment, currentLanguage } from "../context/languageSegment";

export default function RouteManager() {
  const navigate = useNavigate();
  const location = useLocation();
  // Refs keep the handler's identity stable: the subscription is made once and
  // never churns with re-renders, so no navigation event can land in a gap.
  const navigateRef = useRef(navigate);
  const pathnameRef = useRef(location.pathname);
  // Committed values only: a write during render could carry a discarded one.
  useLayoutEffect(() => {
    navigateRef.current = navigate;
    pathnameRef.current = location.pathname;
  }, [navigate, location.pathname]);

  useEffect(() => {
    const changeRouteHandler = (event: Event) => {
      const customEvent = event as CustomEvent;
      const targetPath = customEvent.detail;
      // Module screens emit language-less paths ("/customerOrders/new"); the
      // routes are language-prefixed, so navigating them verbatim mangles the URL.
      let resolved = targetPath;
      if (typeof targetPath === "string" && targetPath.startsWith("/")) {
        if (!isLanguageSegment(targetPath.split("/")[1])) {
          resolved = `/${currentLanguage(pathnameRef.current)}${targetPath}`;
        }
      }
      if (pathnameRef.current !== resolved) {
        navigateRef.current(resolved);
      }
    };
    subscribeWeb("changeRoute", changeRouteHandler);
    return () => {
      unsubscribeWeb("changeRoute", changeRouteHandler);
    };
  }, []);

  return null;
}
